import { noAutoReloadPaths } from 'shared/constants';
import { isWithinPaths } from 'shared/utils/is-within-paths';

const reloadAtStorageKey = 'chunk-load-failure-reload-at';
const reloadCooldownMs = 5 * 60 * 1000;

// Safari, Chrome and Firefox each word a failed dynamic import differently.
// Safari also reports a module that fails to link with this wording.
const chunkLoadErrorMessages = [
  'Importing a module script failed',
  'Importing binding name',
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
];

export const isChunkLoadError = (error: unknown): boolean => {
  const message = (error as { message?: unknown } | null)?.message;
  return (
    typeof message === 'string' &&
    chunkLoadErrorMessages.some((fragment) => message.includes(fragment))
  );
};

// 'false' rules out the worker's cache; 'true' is any returning visit, so it decides nothing on its own.
export const getChunkLoadFailureTags = (
  error: unknown,
): Record<string, string> => {
  if (!isChunkLoadError(error)) {
    return {};
  }
  return {
    chunkLoadFailure: 'true',
    serviceWorkerControlled: String(!!navigator.serviceWorker?.controller),
  };
};

const readLastReloadAt = (): number | null => {
  try {
    return Number(sessionStorage.getItem(reloadAtStorageKey)) || 0;
  } catch {
    return null;
  }
};

// A deploy removes the hashed chunks an open tab still points at; one reload fetches the current build.
export const reloadAfterChunkLoadFailure = (error: unknown): boolean => {
  if (!isChunkLoadError(error)) {
    return false;
  }
  if (isWithinPaths(window.location.pathname, noAutoReloadPaths)) {
    return false;
  }

  // Without a readable guard a failure that survives the reload would loop forever.
  const lastReloadAt = readLastReloadAt();
  const now = Date.now();
  if (lastReloadAt === null || now - lastReloadAt < reloadCooldownMs) {
    return false;
  }

  try {
    sessionStorage.setItem(reloadAtStorageKey, String(now));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
};
